import PropTypes from 'prop-types'
import { sliceStatusPropTypes } from '../slice.types'

/**
 * @typedef {Object} AuthState
 * @property {'login' | 'forgotPassword' | 'resetPassword' | 'verifyPasswordToken' | 'fetchStorageToken' | 'verifyStorageToken' | null} task - The current task of the authentication slice.
 * @property {string} [token] - The current session token.
 * @property {'init' | boolean} tokenVerified - The status of the token verification.
 * @property {{ url: string; external: boolean }} redirectUri - The URI to redirect to after authentication.
 * @property {number | null | 'init'} currentAdminId - The ID of the current admin.
 *
 * @typedef {import('../slice.types').BaseSlice & AuthState} AuthSlice
 */

/** @type {AuthSlice} */
export const initialAuthState = {
  status: 'init',
  errors: [],
  task: null,
  token: '',
  tokenVerified: 'init',
  redirectUri: { url: '/dashboard', external: false },
  currentAdminId: 'init'
}

/** @enum {string} */
export const AUTH_ACTIONS = {
  SET_AUTH_STATUS: 'SET_AUTH_STATUS',
  SET_AUTH_ERRORS: 'SET_AUTH_ERRORS',
  SET_AUTH_TASK: 'SET_AUTH_TASK',
  SET_AUTH_TOKEN: 'SET_AUTH_TOKEN',
  SET_TOKEN_VERIFIED: 'SET_TOKEN_VERIFIED',
  SET_REDIRECT_URI: 'SET_REDIRECT_URI',
  SET_CURRENT_ADMIN_ID: 'SET_CURRENT_ADMIN_ID',

  CLEAR_AUTH_ERRORS: 'CLEAR_AUTH_ERRORS',
  CLEAR_AUTH_TASK: 'CLEAR_AUTH_TASK',
  CLEAR_AUTH_TOKEN: 'CLEAR_AUTH_TOKEN',
  CLEAR_AUTH: 'CLEAR_AUTH',
  OVERWRITE_AUTH: 'OVERWRITE_AUTH',
  UPDATE_AUTH: 'UPDATE_AUTH'
}

export const authTaskPropTypes = PropTypes.oneOf([
  'login',
  'forgotPassword',
  'resetPassword',
  'verifyPasswordToken',
  'verifyStorageToken',
  'fetchStorageToken',
  null
])
export const authTokenPropTypes = PropTypes.string
export const authRedirectUriPropTypes = PropTypes.shape({ url: PropTypes.string, external: PropTypes.bool })
export const authSlicePropTypes = PropTypes.shape({
  status: sliceStatusPropTypes,
  errors: PropTypes.arrayOf(PropTypes.shape({ message: PropTypes.string })),
  task: authTaskPropTypes,
  token: authTokenPropTypes,
  tokenVerified: PropTypes.oneOfType([PropTypes.oneOf(['init']), PropTypes.bool]),
  redirectUri: authRedirectUriPropTypes,
  currentAdminId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['init'])])
})

/**
 * @typedef {{ type: AUTH_ACTIONS, payload: AuthSlice['status'] }} AuthStatusAction
 * @typedef {{ type: AUTH_ACTIONS, payload: AuthSlice['errors'] }} AuthErrorsAction
 * @typedef {{ type: AUTH_ACTIONS, payload: AuthSlice['task'] }} AuthTaskAction
 * @typedef {{ type: AUTH_ACTIONS, payload: AuthSlice['token'] }} AuthTokenAction
 * @typedef {{ type: AUTH_ACTIONS, payload: AuthSlice['tokenVerified'] }} AuthTokenVerifiedAction
 * @typedef {{ type: AUTH_ACTIONS, payload: AuthSlice['redirectUri'] }} AuthRedirectUriAction
 * @typedef {{ type: AUTH_ACTIONS, payload: AuthSlice['currentAdminId'] }} AuthCurrentAdminIdAction
 * @typedef {{ type: AUTH_ACTIONS, payload: Partial<AuthSlice> }} AuthOverwriteAction
 * @typedef {{ type: AUTH_ACTIONS, payload: Partial<AuthSlice> }} AuthUpdateAction
 * @typedef {{ type: AUTH_ACTIONS }} AuthClearAction
 *
 * @typedef {AuthStatusAction | AuthErrorsAction | AuthTaskAction | AuthTokenAction | AuthRedirectUriAction | AuthTokenVerifiedAction | AuthClearAction | AuthOverwriteAction | AuthUpdateAction} AuthAction
 * */
